import { Platform } from 'react-native';

// Conditionally import react-hot-toast only for web platforms
let toast: any = null;
if (Platform.OS === 'web') {
  try {
    toast = require('react-hot-toast').default;
  } catch (error) {
    console.warn('react-hot-toast not available:', error);
  }
}

// Key for storing pending toast messages that should survive navigation
// Used by usePendingToast hook to display toasts after route changes
export const PENDING_TOAST_KEY = 'pendingToast';

// Conditionally import react-native-toast-message only for native platforms
let Toast: any = null;
if (Platform.OS !== 'web') {
  try {
    Toast = require('react-native-toast-message').default;
  } catch (error) {
    console.warn('react-native-toast-message not available:', error);
  }
}

// NOTE: Previously had a global click handler that dismissed ALL toasts on ANY click.
// This was removed because it interfered with showing toasts during navigation.
// Toasts now dismiss via:
// 1. Duration timeout (automatic)
// 2. User clicking directly ON the toast (onClick handler on each toast)
// 3. Programmatic dismiss via showToast.dismiss()

// Cross-platform toast wrapper
export const showToast = {
  success: (message: string, options?: { id?: string }) => {
    if (Platform.OS === 'web' && toast) {
      toast.success(message, {
        duration: 2500,
        // Optional stable id lets repeated callers collapse into one toast.
        ...(options?.id ? { id: options.id } : {}),
        style: {
          cursor: 'pointer',
          backgroundColor: '#ff6b35',
          color: '#fff',
          border: 'none',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#ff6b35',
        },
        onClick: () => {
          toast.dismiss();
        },
      });
    } else if (Toast) {
      Toast.show({
        type: 'success',
        text1: message,
        position: 'top',
        visibilityTime: 2500,
        onPress: () => {
          Toast.hide();
        },
      });
    } else {
      console.warn('Toast not available, falling back to console:', message);
    }
  },

  error: (message: string, options?: { id?: string }) => {
    if (Platform.OS === 'web' && toast) {
      const multiline = message.includes('\n');
      toast.error(message, {
        duration: multiline ? 9000 : 5000,
        // Optional stable id lets repeated callers collapse into one toast.
        ...(options?.id ? { id: options.id } : {}),
        style: {
          cursor: 'pointer',
          ...(multiline
            ? {
                whiteSpace: 'pre-line',
                maxWidth: 420,
                textAlign: 'left' as const,
              }
            : {}),
        },
        onClick: () => {
          toast.dismiss();
        },
      });
    } else if (Toast) {
      // Split message into title and details for better display on native
      const lines = message.split('\n');
      const text1 = lines[0] || message;
      const text2 = lines.length > 1 ? lines.slice(1).join('\n') : undefined;

      Toast.show({
        type: 'error',
        text1: text1,
        text2: text2,
        position: 'top',
        visibilityTime: 8000, // Doubled from 4000ms to 8000ms for better readability
        autoHide: true,
        onPress: () => {
          Toast.hide();
        },
        text1Style: {
          fontSize: 14,
          fontWeight: '600',
        },
        text2Style: {
          fontSize: 13,
          fontWeight: '400',
        },
        text2NumberOfLines: 16, // Bullet lists can exceed 10 lines when many fields fail validation
      });
    } else {
      console.warn('Toast not available, falling back to console:', message);
    }
  },

  loading: (message: string) => {
    if (Platform.OS === 'web' && toast) {
      return toast.loading(message, {
        style: {
          cursor: 'pointer',
        },
        onClick: () => {
          toast.dismiss();
        },
      });
    } else if (Toast) {
      Toast.show({
        type: 'info',
        text1: message,
        position: 'top',
        visibilityTime: 0, // Don't auto-hide loading toasts
        onPress: () => {
          Toast.hide();
        },
      });
      return 'native-toast'; // Return a placeholder ID for consistency
    } else {
      console.warn('Toast not available, falling back to console:', message);
      return 'fallback-toast';
    }
  },

  dismiss: (toastId?: string) => {
    if (Platform.OS === 'web' && toast) {
      if (toastId) {
        toast.dismiss(toastId);
      } else {
        toast.dismiss();
      }
    } else if (Toast) {
      Toast.hide();
    }
  },

  offline: (message: string) => {
    if (Platform.OS === 'web' && toast) {
      return toast(message, {
        // Stable id so concurrent callers collapse into a single toast.
        // useOfflineToast is mounted in several components at once; without a
        // shared id react-hot-toast stacks one notification per instance.
        id: 'offline-toast',
        duration: 10000, // Auto-dismiss after 10 seconds
        style: {
          cursor: 'pointer',
          backgroundColor: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffc107',
        },
        onClick: () => {
          toast.dismiss();
        },
      });
    } else if (Toast) {
      Toast.show({
        type: 'info',
        text1: message,
        position: 'top',
        visibilityTime: 10000, // Auto-dismiss after 10 seconds
        autoHide: true,
        onPress: () => {
          Toast.hide();
        },
      });
      return 'offline-toast';
    } else {
      console.warn('Toast not available, falling back to console:', message);
      return 'fallback-offline-toast';
    }
  },

  info: (message: string, options?: { id?: string }) => {
    if (Platform.OS === 'web' && toast) {
      toast(message, {
        duration: 3000,
        // Optional stable id lets repeated callers collapse into one toast.
        ...(options?.id ? { id: options.id } : {}),
        style: {
          cursor: 'pointer',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          border: '1px solid #f59e0b',
        },
      });
    } else if (Toast) {
      Toast.show({
        type: 'info',
        text1: message,
        position: 'top',
        visibilityTime: 3000,
        autoHide: true,
        onPress: () => {
          Toast.hide();
        },
      });
    } else {
      console.warn('Toast not available, falling back to console:', message);
    }
  },

  /**
   * Show an info toast that persists across page navigation
   * Stores the message in sessionStorage and shows it after navigation completes
   */
  infoPersistent: (message: string) => {
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(
        PENDING_TOAST_KEY,
        JSON.stringify({ type: 'info', message }),
      );
    } else if (Toast) {
      // For native, just show normally since navigation doesn't cause full re-render
      Toast.show({
        type: 'info',
        text1: message,
        position: 'top',
        visibilityTime: 3000,
        autoHide: true,
        onPress: () => {
          Toast.hide();
        },
      });
    } else {
      console.warn('Toast not available, falling back to console:', message);
    }
  },
};

// Export the toast component for rendering
export { Toast };
